import { useEffect } from 'react';

import GiveUsACall from 'components/GiveUsACall';
import LocalBranchMap from 'components/LocalBranchMap/LocalBranchMap';
import FormTitle from 'components/PageLayout/FormTitle';
import { ButtonWithLoadingState } from 'components/curo-components/common/Button';
import { GoogleAnalyticsEventType } from 'enums/GoogleAnalyticsEventType';
import { AvailableOffersTableVariables, CuroApplicationVariables } from 'enums/curo/Variables';
import { getApplicationSelector } from 'handlers/curo-handlers/selectors';
import { useAppSelector } from 'hooks/reduxHooks';
import useGetBranchGeocode from 'hooks/useGetBranchGeocode';
import useGoogleAnalytics from 'hooks/useGoogleAnalytics';
import { TableValue } from 'product_modules/api/Types';
import { ICuroApplication } from 'types/curo/CuroApplication';
import { formatToMonetary } from 'utils/curo-helpers/formatters';

/* eslint-disable no-restricted-imports */
import { BranchOfferType } from '../../constants';

import styles from './BranchEndStep.module.scss';
import BranchInstructions from './BranchInstructions';

const getFormTitle = (application: ICuroApplication, isPhysicalOffer: boolean) => {
  const borrowerFirstName = application.variables[CuroApplicationVariables.BorrowerFirstName];

  if (isPhysicalOffer) {
    const offerAmount = (
      application.variables[CuroApplicationVariables.SelectedLoanOffer] as TableValue
    )[0]?.[AvailableOffersTableVariables.LoanAmount];

    return `Congrats ${borrowerFirstName}, your offer of ${formatToMonetary(
      Number(offerAmount),
    )} is waiting for you at the branch.`;
  }

  return `Congrats ${borrowerFirstName}! You’re on your way to getting a loan.`;
};

interface IBranchEndStepProps {
  branchType: BranchOfferType;
  isPhysicalOffer?: boolean;
}

const BranchEndStep = ({ branchType, isPhysicalOffer = false }: IBranchEndStepProps) => {
  const trackEvent = useGoogleAnalytics();
  const application = useAppSelector(getApplicationSelector)!;
  const { branchPhone, branchStreet, branchCityStateZip, latLng, getDirection } =
    useGetBranchGeocode();

  useEffect(() => {
    if (branchType === BranchOfferType.Physical && !isPhysicalOffer) {
      trackEvent(GoogleAnalyticsEventType.ApplyProcessPhysicalBranch);
    } else if (branchType === BranchOfferType.Central) {
      trackEvent(GoogleAnalyticsEventType.ApplyProcessCentralBranch);
    } else {
      trackEvent(GoogleAnalyticsEventType.ApplyProcessBranchOfferGeneric);
    }
  }, []);

  const renderContent = () => {
    if (branchType === BranchOfferType.Central) {
      return (
        <div className={styles.callContainer}>
          <GiveUsACall />
        </div>
      );
    } else if (latLng?.lat && latLng.lng) {
      return (
        <div className={styles.rightContainer}>
          <LocalBranchMap
            lat={latLng?.lat}
            lng={latLng?.lng}
            branchCityStateZip={branchCityStateZip}
            branchPhone={branchPhone}
            branchStreet={branchStreet}
          />

          <ButtonWithLoadingState kind="primary" size="form" onClick={getDirection}>
            Get Directions
          </ButtonWithLoadingState>
        </div>
      );
    }
  };

  return (
    <div className={styles.pageContainer}>
      <div>
        <FormTitle title={getFormTitle(application, isPhysicalOffer)} />
        <BranchInstructions branchType={branchType} isPhysicalOffer={isPhysicalOffer} />
      </div>

      {renderContent()}
    </div>
  );
};

export default BranchEndStep;
